import PageBackTitle from "../../Component/PageBackTitle";
import React, { useState, useEffect, useRef } from "react";
import { MdOutlineModeEditOutline, MdExpandMore } from "react-icons/md";
import { HiOutlinePlus } from "react-icons/hi";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import { IoSearchOutline, IoEyeOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_URL from "../../Config.js";
import Pagination from "../../Component/Pagination.js";
import useClickOutside from "../../useClickOutside";
import AddPurchaseIndent from "./AddPurchaseIndent";
import EditPurchaseIndent from "./EditPurchaseIndent";
import ViewPurchaseIndent from "./ViewPurchaseIndent";
import "./purchaseIndents.css";

function PurchaseIndentList({ isSidebarExpanded }) {
  const [purchaseIndents, setPurchaseIndents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [isAddIndentOpen, setIsAddIndentOpen] = useState(false);
  const [isEditIndentOpen, setIsEditIndentOpen] = useState(false);
  const [isViewIndentOpen, setIsViewIndentOpen] = useState(false);
  const [selectedIndentId, setSelectedIndentId] = useState(null);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const fetchTimeout = useRef(null);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  // Filter states
  const [filters, setFilters] = useState({
    customer_id: "",
    status: "",
    from_date: "",
    to_date: "",
  });

  // const statusOptions = [
  //   { value: "Draft", label: "Draft", color: "#6c757d" },
  //   { value: "Pending Approval", label: "Pending Approval", color: "#FFA500" },
  //   { value: "Approved", label: "Approved", color: "#28a745" },
  //   { value: "Completed", label: "Completed", color: "#007bff" },
  // ];

  const statusOptions = [
    { value: "Indent", label: "Indent", color: "#64748b" },
    { value: "Approved", label: "Approved", color: "#3b82f6" },
    { value: "Dispatched", label: "Dispatched", color: "#8b5cf6" },
    { value: "Completed", label: "Completed", color: "#22c55e" },
  ];

  // Handle outside click for popup
  useClickOutside(
    [containerRef, buttonRef],
    () => {
      setIsSortPopup(false);
      setSelectedRowIndex(null);
    },
    200
  );

  useClickOutside(
    [popupRef, buttonRef],
    () => {
      setIsSortPopup(false);
      setSelectedRowIndex(null);
    },
    300
  );

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const fetchData = async (
    currentPage = page,
    perPage = itemsPerPage,
    search = searchInput,
    currentFilters = filters
  ) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const params = {
        page: currentPage,
        per_page: perPage,
      };

      if (search && search.trim() !== "") params.search = search.trim();
      if (currentFilters.customer_id)
        params.customer_id = currentFilters.customer_id;
      if (currentFilters.status) params.status = currentFilters.status;
      if (currentFilters.from_date) params.from_date = currentFilters.from_date;
      if (currentFilters.to_date) params.to_date = currentFilters.to_date;

      const response = await axios.get(`${API_URL}/api/barrel/indent`, {
        headers: {
          Authorization: authKey,
        },
        params,
      });

      const data = response.data;
      setPurchaseIndents(Array.isArray(data.indents) ? data.indents : []);
      setTotalItems(data.totalItems ?? 0);
    } catch (error) {
      console.error("Error fetching purchase indents:", error);
      showSnackbar("Failed to fetch purchase indents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(() => {
      fetchData(page, itemsPerPage, searchInput, filters);
    }, 400);
    return () => clearTimeout(fetchTimeout.current);
  }, [page, itemsPerPage, searchInput, filters]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      if (!authKey) {
        showSnackbar("Authorization key missing");
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/barrel/customers/minimal`,
        {
          headers: { Authorization: authKey },
        }
      );

      const customerData = response.data?.customers ?? [];
      const formattedCustomers = customerData.map((cust) => ({
        id: cust.id,
        customer_name: cust.customer_name,
      }));
      setCustomers(formattedCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
      showSnackbar("Error loading customers");
    }
  };

  const handlePaginationChange = (currentPage, perPage) => {
    setPage(currentPage);
    setItemsPerPage(perPage);
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("itemsPerPage", perPage);
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
    setPage(1);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      customer_id: "",
      status: "",
      from_date: "",
      to_date: "",
    });
    setSearchInput("");
    setPage(1);
  };

  const handleRefresh = () => {
    handleClearFilters();
    fetchData(1, itemsPerPage, "", {
      customer_id: "",
      status: "",
      from_date: "",
      to_date: "",
    });
  };

  const handleButtonClick = (index, event) => {
    event.stopPropagation();
    setSelectedRowIndex(index === selectedRowIndex ? null : index);
  };

  const handleOpenAddIndent = () => setIsAddIndentOpen(true);
  const handleCloseAddIndent = () => {
    setIsAddIndentOpen(false);
    fetchData(page, itemsPerPage, searchInput, filters);
  };

  const handleOpenEditIndent = (indentId) => {
    setSelectedIndentId(indentId);
    setIsEditIndentOpen(true);
    setSelectedRowIndex(null);
  };

  const handleCloseEditIndent = () => {
    setIsEditIndentOpen(false);
    setSelectedIndentId(null);
    fetchData(page, itemsPerPage, searchInput, filters);
  };

  const handleOpenViewIndent = (indentId) => {
    setSelectedIndentId(indentId);
    setIsViewIndentOpen(true);
    setSelectedRowIndex(null);
  };

  const handleCloseViewIndent = () => {
    setIsViewIndentOpen(false);
    setSelectedIndentId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "NA") return "NA";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = statusOptions.find((s) => s.value === status) || {
      color: "#6c757d",
      label: status || "Unknown",
    };

    return (
      <span
        style={{
          backgroundColor: `${statusConfig.color}20`,
          color: statusConfig.color,
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
          display: "inline-block",
          border: `1px solid ${statusConfig.color}40`,
        }}
      >
        {statusConfig.label}
      </span>
    );
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content" ref={containerRef}>
          {/* Header Section */}
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>All Purchase Indents</PageBackTitle>

              <div className="data-list-heading-content-right-side">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search by indent number..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    className="search-bar"
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>

                <Tooltip title="New Purchase Indent" arrow>
                  <button className="new-button" onClick={handleOpenAddIndent}>
                    <HiOutlinePlus className="new-plus-icon" /> New
                  </button>
                </Tooltip>

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>

                <button
                  className="sort-button"
                  onClick={() => setIsSortPopup((s) => !s)}
                  ref={buttonRef}
                >
                  <PiDotsThreeOutlineVerticalFill className="sort-icon" />
                </button>

                {/* Sort Popup */}
                {isSortPopup && (
                  <div className="sort-popup-container" ref={containerRef}>
                    <div className="sort-popup-content">
                      <div className="sort-by">SORT BY</div>
                      <div className="sort-by-button">
                        <button
                          onClick={() => {
                            const sorted = [...purchaseIndents].sort((a, b) =>
                              (a.indent_number || "").localeCompare(
                                b.indent_number || ""
                              )
                            );
                            setPurchaseIndents(sorted);
                            setIsSortPopup(false);
                          }}
                        >
                          Indent Number
                        </button>
                        <br />
                        <button
                          onClick={() => {
                            const sorted = [...purchaseIndents].sort((a, b) =>
                              (a.customer_name || "").localeCompare(
                                b.customer_name || ""
                              )
                            );
                            setPurchaseIndents(sorted);
                            setIsSortPopup(false);
                          }}
                        >
                          Customer Name
                        </button>
                        <br />
                        <button
                          onClick={() => {
                            const sorted = [...purchaseIndents].sort(
                              (a, b) =>
                                new Date(b.indent_date) -
                                new Date(a.indent_date)
                            );
                            setPurchaseIndents(sorted);
                            setIsSortPopup(false);
                          }}
                        >
                          Indent Date
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Filter Card */}
            <div
              className="filter-card"
              style={{
                width: "100%",
                background: "#fff",
                border: "1px solid #e0e0e0",
                // borderRadius: "10px",
                padding: "12px 20px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "20px",
              }}
            >
              {/* Customer Filter */}
              <select
                value={filters.customer_id}
                onChange={(e) =>
                  handleFilterChange("customer_id", e.target.value)
                }
                // className="filter-dropdown"
                className="newdashboard-select"
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  minWidth: "150px",
                  flex: "1 1 150px",
                }}
              >
                <option value="">All Customers</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.customer_name}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="newdashboard-select"
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  minWidth: "150px",
                  flex: "1 1 150px",
                }}
              >
                <option value="">All Status</option>
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>

              {/* From Date */}
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) =>
                  handleFilterChange("from_date", e.target.value)
                }
                className="filter-dropdown"
                placeholder="From Date"
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  minWidth: "150px",
                  flex: "1 1 150px",
                }}
              />

              {/* To Date */}
              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => handleFilterChange("to_date", e.target.value)}
                className="filter-dropdown"
                placeholder="To Date"
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  minWidth: "150px",
                  flex: "1 1 150px",
                }}
              />
            </div>
          </div>

          {/* Table Body */}
          <div
            className="data-list-body"
            onClick={() => {
              setIsSortPopup(false);
              setSelectedRowIndex(null);
            }}
          >
            <div className="data-list-scroll-body-purchase_indent">
              <div className="data-list-tables">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : purchaseIndents.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        <th>INDENT NUMBER</th>
                        <th>CUSTOMER NAME</th>
                        <th>SAP CODE</th>
                        <th>CUSTOMER TYPE</th>
                        <th>TOTAL QTY (KL)</th>
                        <th>STATUS</th>
                        <th>REQUEST DATE</th>
                        <th>INDENT DATE</th>
                        <th>EXPECTED DELIVERY</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {purchaseIndents.map((indent, index) => (
                        <tr key={indent.id} className="hoverable-row">
                          <td className="center-alignment">
                            {(page - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="view-data">
                            {indent.indent_number || "NA"}
                          </td>
                          <td>{indent.customer_name || "NA"}</td>
                          <td>{indent.sap_code || "NA"}</td>
                          <td>{indent.customer_type || "NA"}</td>
                          <td>
                            {indent.total_quantity
                              ? parseFloat(indent.total_quantity).toFixed(2)
                              : "0.00"}
                          </td>
                          <td>{getStatusBadge(indent.indent_status)}</td>
                          <td>{formatDate(indent.created_at)}</td>
                          <td>{formatDate(indent.indent_date)}</td>
                          <td>{formatDate(indent.expected_delivery_date)}</td>

                          <td className="edit-pop">
                            <div className="purchase_indent_actions">
                              <button
                                className="purchase_indent_view_btn"
                                onClick={() => handleOpenViewIndent(indent.id)}
                              >
                                View
                              </button>

                              <button
                                className="purchase_indent_edit_btn"
                                onClick={() => handleOpenEditIndent(indent.id)}
                              >
                                {" "}
                                Edit
                              </button>
                            </div>
                          </td>

                          {selectedRowIndex === index && (
                            <div className="edit-popup" ref={popupRef}>
                              <div className="edit-popup-contant">
                                <div className="edit-popup-button">
                                  <button
                                    className="edit-button-inquiry"
                                    onClick={() =>
                                      handleOpenViewIndent(indent.id)
                                    }
                                  >
                                    <IoEyeOutline /> View
                                  </button>
                                  <button
                                    className="edit-button-inquiry"
                                    onClick={() =>
                                      handleOpenEditIndent(indent.id)
                                    }
                                  >
                                    <MdOutlineModeEditOutline /> Edit
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No Purchase Indents Found
                    </div>
                  </div>
                )}
              </div>

              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Purchase Indent Popup */}
      {isAddIndentOpen && (
        <AddPurchaseIndent
          onClose={handleCloseAddIndent}
          onSuccess={handleCloseAddIndent}
          showSnackbar={showSnackbar}
        />
      )}

      {/* Edit Purchase Indent Popup */}
      {isEditIndentOpen && (
        <EditPurchaseIndent
          onClose={handleCloseEditIndent}
          onSuccess={handleCloseEditIndent}
          showSnackbar={showSnackbar}
          indentId={selectedIndentId}
        />
      )}

      {/* View Purchase Indent Popup */}
      {isViewIndentOpen && (
        <ViewPurchaseIndent
          onClose={handleCloseViewIndent}
          indentId={selectedIndentId}
          showSnackbar={showSnackbar}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity="success">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default PurchaseIndentList;
